/**
 * A component that renders radio button options for selecting question set types.
 **/
import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Choice, RadioGroup} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, LabelLarge} from "@khanacademy/wonder-blocks-typography";
import type {Assignment, QuestionSetTypeEnum} from "@khan/assignments-types";
import {useFlagsCookie} from "@khan/data-cookies";

import {
    containsExerciseContent,
    containsExerciseContentOnly,
    isExerciseContent,
} from "../util.tsx";

export interface AssignmentQuestionSetUpdateProps {
    questionSetType: QuestionSetTypeEnum;
}

interface QuestionSetProps {
    assignment: Assignment;
    qualifiedContentDescriptorsList: Array<string>;
    onAssignmentUpdate: (props: AssignmentQuestionSetUpdateProps) => void;
    hasGroupedExercises: boolean;
    displayStudentAndChildCopy: boolean;
}

const QuestionSetRadio = (props: QuestionSetProps): React.ReactNode => {
    const {
        assignment: {contentDescriptors, questionSetType},
        qualifiedContentDescriptorsList,
        hasGroupedExercises,
        displayStudentAndChildCopy,
        onAssignmentUpdate,
    } = props;

    // Use hooks for feature flag and cookie data
    const cookieFlagResult = useFlagsCookie("free-bonus-problem-frontend");
    const featureIsOn = useFeatureIsOn("free-bonus-problem-frontend");

    // Effect to handle grouped exercises logic (replaces componentDidMount and componentDidUpdate)
    useEffect(() => {
        if (hasGroupedExercises) {
            // Exercises that have grouped assessment items ("grouped exercises")
            // only have one set of questions so they are same question
            // assignments by default.
            onAssignmentUpdate({questionSetType: "SAME"});
        }
    }, [hasGroupedExercises, onAssignmentUpdate]);

    const onQuestionSetTypeSelect = (option: QuestionSetTypeEnum): void => {
        onAssignmentUpdate({questionSetType: option});
    };

    const getLabelAndDescriptions = (
        displayStudentAndChildCopy: boolean,
        isFreeBonusProblemEnabled: boolean,
    ): Record<string, string> => {
        if (displayStudentAndChildCopy) {
            return {
                differentQuestionSetLabel: t`Different question set for each student or child`,
                differentQuestionSetDescription: t`Each student or child will work on a random set of questions, shown in random order.`,
                sameQuestionSetLabel: t`Same question set for all students and children`,
                sameQuestionSetDescription: isFreeBonusProblemEnabled
                    ? t`Students and children will all work on the same set of questions, shown in the same order, without bonus questions.`
                    : t`Students and children will all work on the same set of questions, shown in the same order.`,
            };
        }
        return {
            differentQuestionSetLabel: t`Different question set for each student`,
            differentQuestionSetDescription: t`Each student will work on a random set of questions, shown in random order.`,
            sameQuestionSetLabel: t`Same question set for all students`,
            sameQuestionSetDescription: isFreeBonusProblemEnabled
                ? t`Students will all work on the same set of questions, shown in the same order, without bonus questions.`
                : t`Students will all work on the same set of questions, shown in the same order.`,
        };
    };

    const questionSetTypeAsValue = questionSetType || ("RANDOM" as string);

    const hasExerciseContent =
        qualifiedContentDescriptorsList.length === 0
            ? isExerciseContent(contentDescriptors)
            : containsExerciseContent(qualifiedContentDescriptorsList);

    // Exercises that have grouped assessment items ("grouped exercises")
    // only have one set of questions so they are same question
    // assignments by default. Since there's only one option, we hide the
    // radio buttons, and set questionSetType to "SAME" above.
    if (!hasExerciseContent || hasGroupedExercises) {
        return null;
    }

    const hasExerciseContentOnly =
        qualifiedContentDescriptorsList.length === 0
            ? isExerciseContent(contentDescriptors)
            : containsExerciseContentOnly(qualifiedContentDescriptorsList);

    const freeBonusProblemCookieFlag =
        cookieFlagResult.status === "success" ? cookieFlagResult.value : false;

    const isFreeBonusProblemEnabled =
        typeof freeBonusProblemCookieFlag === "boolean"
            ? freeBonusProblemCookieFlag
            : featureIsOn;

    const {
        differentQuestionSetLabel,
        differentQuestionSetDescription,
        sameQuestionSetLabel,
        sameQuestionSetDescription,
    } = getLabelAndDescriptions(
        displayStudentAndChildCopy,
        isFreeBonusProblemEnabled,
    );

    return (
        <View>
            <View style={{flexDirection: "row"}}>
                <LabelLarge style={styles.questionSetLabel}>
                    {t`Question set`}
                </LabelLarge>
                {!hasExerciseContentOnly && (
                    <Body style={styles.container}>
                        {t`(only applies to exercises)`}
                    </Body>
                )}
            </View>
            <Strut size={spacing.xSmall_8} />
            <RadioGroup
                groupName="select-question-set-type"
                onChange={(value: any) => {
                    const selectedValue = value as QuestionSetTypeEnum;
                    onQuestionSetTypeSelect(selectedValue);
                }}
                selectedValue={questionSetTypeAsValue}
            >
                {!hasGroupedExercises && (
                    <Choice
                        label={differentQuestionSetLabel}
                        description={differentQuestionSetDescription}
                        value="RANDOM"
                        testId="select-different-question-set"
                    />
                )}
                <Choice
                    label={sameQuestionSetLabel}
                    description={sameQuestionSetDescription}
                    value="SAME"
                    testId="select-same-question-set"
                />
            </RadioGroup>
            <Strut size={spacing.xLarge_32} />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        float: "left",
    },
    questionSetLabel: {
        display: "block",
        float: "left",
        marginRight: spacing.xSmall_8,
    },
});

export default QuestionSetRadio;
